import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { api } from '../lib/api';

const ACCENT = '#f97316';

interface ROISummary {
  total_executions: number;
  total_records: number;
  success_rate: number;
  total_manual_hours_saved: number;
  hourly_rate: number;
  total_cost_savings: number;
  most_active_flow: string;
  most_error_prone_flow: string;
}

interface FlowRow {
  flow_id: string;
  flow_name: string;
  executions: number;
  success_rate: number;
  total_records: number;
  avg_duration_ms: number;
  hours_saved: number;
  cost_savings: number;
}

interface TrendPoint {
  date: string;
  executions: number;
  errors: number;
  records: number;
}

type SortKey = 'flow_name' | 'executions' | 'success_rate' | 'total_records' | 'avg_duration_ms' | 'hours_saved' | 'cost_savings';

export default function AnalyticsPage() {
  const [summary, setSummary] = useState<ROISummary | null>(null);
  const [flows, setFlows] = useState<FlowRow[]>([]);
  const [trends, setTrends] = useState<TrendPoint[]>([]);
  const [hourlyRate, setHourlyRate] = useState(75);
  const [rateInput, setRateInput] = useState('75');
  const [loading, setLoading] = useState(true);
  const [sortKey, setSortKey] = useState<SortKey>('executions');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');
  const [hoveredDay, setHoveredDay] = useState<number | null>(null);
  const [saving, setSaving] = useState(false);

  const loadData = useCallback(async () => {
    try {
      const [overviewRes, flowsRes, trendsRes] = await Promise.all([
        api.get<{ summary: ROISummary }>('/analytics/overview'),
        api.get<{ flows: FlowRow[]; hourly_rate: number }>('/analytics/flows'),
        api.get<{ trends: TrendPoint[] }>('/analytics/trends'),
      ]);
      setSummary(overviewRes.summary);
      setFlows(flowsRes.flows);
      setTrends(trendsRes.trends);
      setHourlyRate(flowsRes.hourly_rate);
      setRateInput(String(flowsRes.hourly_rate));
    } catch {
      // API errors handled by UI state
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  const handleSaveRate = async () => {
    const rate = parseFloat(rateInput);
    if (isNaN(rate) || rate < 0) return;
    setSaving(true);
    try {
      await api.patch('/analytics/settings', { hourly_rate: rate });
      setHourlyRate(rate);
      await loadData();
    } catch {
      // Save failure handled silently
    } finally {
      setSaving(false);
    }
  };

  const sortedFlows = useMemo(() => {
    const sorted = [...flows];
    sorted.sort((a, b) => {
      const aVal = a[sortKey];
      const bVal = b[sortKey];
      if (typeof aVal === 'string') return aVal.localeCompare(bVal as string) * (sortDir === 'asc' ? 1 : -1);
      return ((aVal as number) - (bVal as number)) * (sortDir === 'asc' ? 1 : -1);
    });
    return sorted;
  }, [flows, sortKey, sortDir]);

  const handleSort = (key: SortKey) => {
    if (sortKey === key) {
      setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    } else {
      setSortKey(key);
      setSortDir('desc');
    }
  };

  const sortArrow = (key: SortKey) => sortKey === key ? (sortDir === 'asc' ? ' ↑' : ' ↓') : '';

  // Chart calculations
  const chartWidth = 800;
  const chartHeight = 200;
  const chartPadding = { top: 20, right: 20, bottom: 30, left: 50 };
  const innerW = chartWidth - chartPadding.left - chartPadding.right;
  const innerH = chartHeight - chartPadding.top - chartPadding.bottom;

  const maxExec = Math.max(1, ...trends.map(t => t.executions));
  const maxErr = Math.max(1, ...trends.map(t => t.errors));
  const maxY = Math.max(maxExec, maxErr);

  const execPath = useMemo(() => {
    if (trends.length === 0) return '';
    const points = trends.map((t, i) => {
      const x = chartPadding.left + (i / Math.max(1, trends.length - 1)) * innerW;
      const y = chartPadding.top + innerH - (t.executions / maxY) * innerH;
      return `${x},${y}`;
    });
    return `M${points.join('L')}`;
  }, [trends, maxY, innerW, innerH]);

  const execAreaPath = useMemo(() => {
    if (trends.length === 0) return '';
    const baseline = chartPadding.top + innerH;
    const points = trends.map((t, i) => {
      const x = chartPadding.left + (i / Math.max(1, trends.length - 1)) * innerW;
      const y = chartPadding.top + innerH - (t.executions / maxY) * innerH;
      return `${x},${y}`;
    });
    const firstX = chartPadding.left;
    const lastX = chartPadding.left + innerW;
    return `M${firstX},${baseline}L${points.join('L')}L${lastX},${baseline}Z`;
  }, [trends, maxY, innerW, innerH]);

  const errorPath = useMemo(() => {
    if (trends.length === 0) return '';
    const points = trends.map((t, i) => {
      const x = chartPadding.left + (i / Math.max(1, trends.length - 1)) * innerW;
      const y = chartPadding.top + innerH - (t.errors / maxY) * innerH;
      return `${x},${y}`;
    });
    return `M${points.join('L')}`;
  }, [trends, maxY, innerW, innerH]);

  if (loading) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: 400 }}>
        <p style={{ color: '#6b7280' }}>Loading analytics...</p>
      </div>
    );
  }

  return (
    <div>
      <div style={{ marginBottom: '1.5rem' }}>
        <h1 style={{ fontSize: '1.5rem', fontWeight: 700, margin: 0 }}>Flow Analytics & ROI</h1>
        <p style={{ color: '#6b7280', margin: '0.25rem 0 0', fontSize: '0.875rem' }}>
          Last 30 days of integration flow performance and return on investment
        </p>
      </div>

      {/* Top row — 4 ROI stat cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '1rem', marginBottom: '1.5rem' }}>
        <StatCard
          label="Total Executions"
          value={summary?.total_executions.toLocaleString() || '0'}
          sub="Last 30 days"
          color="#3b82f6"
        />
        <StatCard
          label="Records Processed"
          value={summary?.total_records.toLocaleString() || '0'}
          sub="Last 30 days"
          color="#8b5cf6"
        />
        <StatCard
          label="Hours Saved"
          value={`${summary?.total_manual_hours_saved.toFixed(1) || '0'}h`}
          sub={`@ $${hourlyRate}/hr`}
          color="#10b981"
        />
        <StatCard
          label="Cost Savings"
          value={`$${(summary?.total_cost_savings || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}
          sub={`~$${Math.round((summary?.total_cost_savings || 0) * 12).toLocaleString()}/yr projected`}
          color={ACCENT}
        />
      </div>

      {/* Chart row — SVG area chart */}
      <div style={{
        backgroundColor: '#fff',
        borderRadius: '0.5rem',
        border: '1px solid #e5e7eb',
        padding: '1rem',
        marginBottom: '1.5rem',
      }}>
        <h2 style={{ fontSize: '1rem', fontWeight: 600, margin: '0 0 0.75rem' }}>Execution Trends</h2>
        <div style={{ display: 'flex', gap: '1rem', fontSize: '0.75rem', color: '#6b7280', marginBottom: '0.5rem' }}>
          <span><span style={{ display: 'inline-block', width: 12, height: 12, backgroundColor: 'rgba(16, 185, 129, 0.2)', border: '2px solid #10b981', borderRadius: 2, marginRight: 4, verticalAlign: 'middle' }} />Executions</span>
          <span><span style={{ display: 'inline-block', width: 12, height: 2, backgroundColor: '#ef4444', marginRight: 4, verticalAlign: 'middle' }} />Errors</span>
        </div>
        <svg width="100%" viewBox={`0 0 ${chartWidth} ${chartHeight}`} style={{ overflow: 'visible' }}>
          {/* Y axis grid lines */}
          {[0, 0.25, 0.5, 0.75, 1].map(frac => {
            const y = chartPadding.top + innerH * (1 - frac);
            return (
              <g key={frac}>
                <line x1={chartPadding.left} y1={y} x2={chartWidth - chartPadding.right} y2={y} stroke="#e5e7eb" strokeDasharray="4" />
                <text x={chartPadding.left - 8} y={y + 4} textAnchor="end" fontSize={10} fill="#9ca3af">
                  {Math.round(maxY * frac)}
                </text>
              </g>
            );
          })}

          {/* Area fill */}
          <path d={execAreaPath} fill="rgba(16, 185, 129, 0.15)" />

          {/* Execution line */}
          <path d={execPath} fill="none" stroke="#10b981" strokeWidth={2} />

          {/* Error line */}
          <path d={errorPath} fill="none" stroke="#ef4444" strokeWidth={2} strokeDasharray="4" />

          {/* X axis labels — show every 5th day */}
          {trends.map((t, i) => {
            if (i % 5 !== 0 && i !== trends.length - 1) return null;
            const x = chartPadding.left + (i / Math.max(1, trends.length - 1)) * innerW;
            const label = t.date.slice(5); // MM-DD
            return (
              <text key={t.date} x={x} y={chartHeight - 5} textAnchor="middle" fontSize={10} fill="#9ca3af">
                {label}
              </text>
            );
          })}

          {/* Hover zones */}
          {trends.map((t, i) => {
            const x = chartPadding.left + (i / Math.max(1, trends.length - 1)) * innerW;
            const barW = Math.max(8, innerW / trends.length);
            return (
              <rect
                key={`hover-${t.date}`}
                x={x - barW / 2}
                y={chartPadding.top}
                width={barW}
                height={innerH}
                fill="transparent"
                onMouseEnter={() => setHoveredDay(i)}
                onMouseLeave={() => setHoveredDay(null)}
              />
            );
          })}

          {/* Tooltip */}
          {hoveredDay !== null && trends[hoveredDay] && (() => {
            const t = trends[hoveredDay];
            const x = chartPadding.left + (hoveredDay / Math.max(1, trends.length - 1)) * innerW;
            const y = chartPadding.top + innerH - (t.executions / maxY) * innerH;
            const tooltipX = x > chartWidth / 2 ? x - 130 : x + 10;
            return (
              <g>
                <line x1={x} y1={chartPadding.top} x2={x} y2={chartPadding.top + innerH} stroke="#d1d5db" strokeDasharray="2" />
                <circle cx={x} cy={y} r={4} fill="#10b981" />
                <rect x={tooltipX} y={chartPadding.top} width={120} height={54} rx={4} fill="#1f2937" opacity={0.95} />
                <text x={tooltipX + 8} y={chartPadding.top + 16} fontSize={11} fill="#fff" fontWeight={600}>{t.date}</text>
                <text x={tooltipX + 8} y={chartPadding.top + 32} fontSize={10} fill="#10b981">Execs: {t.executions}</text>
                <text x={tooltipX + 8} y={chartPadding.top + 46} fontSize={10} fill="#ef4444">Errors: {t.errors}</text>
              </g>
            );
          })()}
        </svg>
      </div>

      {/* Per-flow table */}
      <div style={{
        backgroundColor: '#fff',
        borderRadius: '0.5rem',
        border: '1px solid #e5e7eb',
        padding: '1rem',
        marginBottom: '1.5rem',
      }}>
        <h2 style={{ fontSize: '1rem', fontWeight: 600, margin: '0 0 0.75rem' }}>Per-Flow Breakdown</h2>
        {sortedFlows.length === 0 ? (
          <p style={{ color: '#6b7280', fontSize: '0.875rem', textAlign: 'center', padding: '2rem' }}>
            No flow analytics data yet. Run some integration flows to see data here.
          </p>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.875rem' }}>
              <thead>
                <tr style={{ borderBottom: '2px solid #e5e7eb' }}>
                  {([
                    ['flow_name', 'Flow Name'],
                    ['executions', 'Executions'],
                    ['success_rate', 'Success Rate'],
                    ['total_records', 'Records'],
                    ['avg_duration_ms', 'Avg Duration'],
                    ['hours_saved', 'Hours Saved'],
                    ['cost_savings', 'Cost Savings'],
                  ] as [SortKey, string][]).map(([key, label]) => (
                    <th
                      key={key}
                      onClick={() => handleSort(key)}
                      style={{
                        padding: '0.5rem 0.75rem',
                        textAlign: key === 'flow_name' ? 'left' : 'right',
                        fontWeight: 600,
                        color: '#374151',
                        cursor: 'pointer',
                        userSelect: 'none',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {label}{sortArrow(key)}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {sortedFlows.map(f => (
                  <tr key={f.flow_id} style={{ borderBottom: '1px solid #f3f4f6' }}>
                    <td style={{ padding: '0.5rem 0.75rem', fontWeight: 500 }}>{f.flow_name}</td>
                    <td style={{ padding: '0.5rem 0.75rem', textAlign: 'right' }}>{f.executions.toLocaleString()}</td>
                    <td style={{ padding: '0.5rem 0.75rem', textAlign: 'right' }}>
                      <span style={{
                        color: f.success_rate >= 95 ? '#10b981' : f.success_rate >= 80 ? '#f59e0b' : '#ef4444',
                        fontWeight: 600,
                      }}>
                        {f.success_rate}%
                      </span>
                    </td>
                    <td style={{ padding: '0.5rem 0.75rem', textAlign: 'right' }}>{f.total_records.toLocaleString()}</td>
                    <td style={{ padding: '0.5rem 0.75rem', textAlign: 'right' }}>
                      {f.avg_duration_ms < 1000 ? `${f.avg_duration_ms}ms` : `${(f.avg_duration_ms / 1000).toFixed(1)}s`}
                    </td>
                    <td style={{ padding: '0.5rem 0.75rem', textAlign: 'right' }}>{f.hours_saved.toFixed(1)}h</td>
                    <td style={{ padding: '0.5rem 0.75rem', textAlign: 'right', fontWeight: 600, color: '#10b981' }}>
                      ${f.cost_savings.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* ROI Configuration panel */}
      <div style={{
        backgroundColor: '#fff',
        borderRadius: '0.5rem',
        border: '1px solid #e5e7eb',
        padding: '1rem',
      }}>
        <h2 style={{ fontSize: '1rem', fontWeight: 600, margin: '0 0 0.75rem' }}>ROI Configuration</h2>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
          <label style={{ fontSize: '0.875rem', color: '#374151', fontWeight: 500 }}>
            Hourly Rate ($):
          </label>
          <input
            type="number"
            value={rateInput}
            onChange={e => setRateInput(e.target.value)}
            min={0}
            max={10000}
            step={5}
            style={{
              width: 100,
              padding: '0.375rem 0.5rem',
              border: '1px solid #d1d5db',
              borderRadius: '0.375rem',
              fontSize: '0.875rem',
            }}
          />
          <button
            onClick={handleSaveRate}
            disabled={saving}
            style={{
              padding: '0.375rem 1rem',
              backgroundColor: ACCENT,
              color: '#fff',
              border: 'none',
              borderRadius: '0.375rem',
              fontSize: '0.875rem',
              fontWeight: 600,
              cursor: saving ? 'not-allowed' : 'pointer',
              opacity: saving ? 0.7 : 1,
            }}
          >
            {saving ? 'Saving...' : 'Update Rate'}
          </button>
          <span style={{ fontSize: '0.75rem', color: '#9ca3af' }}>
            Used to calculate cost savings (records x 0.5min / 60 x rate)
          </span>
        </div>
        {summary && (
          <div style={{ marginTop: '1rem', display: 'flex', gap: '2rem', fontSize: '0.8125rem', color: '#6b7280' }}>
            <span>Most active: <strong style={{ color: '#374151' }}>{summary.most_active_flow}</strong></span>
            <span>Most error-prone: <strong style={{ color: '#ef4444' }}>{summary.most_error_prone_flow}</strong></span>
            <span>Success rate: <strong style={{ color: summary.success_rate >= 95 ? '#10b981' : '#f59e0b' }}>{summary.success_rate}%</strong></span>
          </div>
        )}
      </div>
    </div>
  );
}

function StatCard({ label, value, sub, color }: { label: string; value: string; sub: string; color: string }) {
  return (
    <div style={{
      backgroundColor: '#fff',
      borderRadius: '0.5rem',
      border: '1px solid #e5e7eb',
      padding: '1rem',
      borderTop: `3px solid ${color}`,
    }}>
      <p style={{ fontSize: '0.75rem', color: '#6b7280', margin: 0, textTransform: 'uppercase', letterSpacing: '0.05em', fontWeight: 600 }}>
        {label}
      </p>
      <p style={{ fontSize: '1.5rem', fontWeight: 700, margin: '0.25rem 0', color: '#111827' }}>
        {value}
      </p>
      <p style={{ fontSize: '0.75rem', color: '#9ca3af', margin: 0 }}>
        {sub}
      </p>
    </div>
  );
}
